import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMedia } from '../api/mediaApi';
import { fetchGallery } from '../api/galleryApi';
import { placeholderSvgDataUrl } from '../utils/placeholderImage';

const Media = () => {
  const [type, setType] = useState('all');
  const [category, setCategory] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [broken, setBroken] = useState(() => new Set());

  const markBroken = (id) => {
    setBroken((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const [mediaData, galleryData] = await Promise.all([
          fetchMedia({ active: true, limit: 50 }),
          fetchGallery({ limit: 50 }),
        ]);
        if (!mounted) return;
        const mediaItems = Array.isArray(mediaData?.items) ? mediaData.items : [];
        const galleryItems = (Array.isArray(galleryData?.items) ? galleryData.items : [])
          .map((g) => ({
            ...g,
            id: `g-${g.id}`,
            type: 'image',
            file_url: g.url,
            title: g.title || 'Gallery image',
            isGallery: true,
          }));
        setItems([...galleryItems, ...mediaItems]);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load media');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((m) => {
      if (m.category) set.add(m.category);
    });
    return ['all', ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((m) => {
      if (broken.has(m.id)) return false;
      if (type !== 'all' && m.type !== type) return false;
      if (category !== 'all' && String(m.category || '') !== category) return false;
      return true;
    });
  }, [items, type, category, broken]);

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: `
        .media-hero { position: relative; overflow: hidden; background: linear-gradient(180deg, #080c14 0%, #0d1220 40%, #111827 100%); }
        .media-hero::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 600px 400px at 50% 0%, rgba(255,215,0,0.07), transparent 70%), radial-gradient(ellipse 300px 300px at 85% 50%, rgba(255,215,0,0.04), transparent 70%); pointer-events: none; }
        .media-hero-inner { position: relative; z-index: 1; }

        .media-hero-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,215,0,0.1); border: 1px solid rgba(255,215,0,0.2); border-radius: 50px; padding: 6px 16px; font-size: 13px; color: var(--accent); font-weight: 600; margin-bottom: 16px; animation: mBadgeIn 0.6s ease; }
        @keyframes mBadgeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }

        .media-hero-title { font-size: clamp(2rem, 4vw, 3.5rem); font-weight: 900; color: #fff; margin: 0 0 12px; line-height: 1.2; animation: mTitleIn 0.7s ease; }
        .media-hero-title span { background: linear-gradient(135deg, var(--accent), #e6c200); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        @keyframes mTitleIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

        .media-hero-subtitle { font-size: clamp(0.9rem, 1.5vw, 1.1rem); color: rgba(255,255,255,0.6); margin: 0 auto 24px; max-width: 500px; line-height: 1.6; animation: mSubIn 0.8s ease; }
        @keyframes mSubIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

        .media-hero-filters { display: flex; gap: 8px; justify-content: center; animation: mFiltersIn 0.9s ease; flex-wrap: wrap; }
        @keyframes mFiltersIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .media-filter-pill { padding: 10px 20px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.6); font-size: 14px; cursor: pointer; font-weight: 600; transition: all 0.25s ease; text-transform: capitalize; }
        .media-filter-pill:hover { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); transform: translateY(-2px); }
        .media-filter-pill.active { background: var(--accent); color: #000; border-color: var(--accent); font-weight: 700; box-shadow: 0 4px 14px rgba(255,215,0,0.25); }

        .media-hero-stats { display: flex; gap: 32px; justify-content: center; margin-top: 28px; flex-wrap: wrap; animation: mStatsIn 1s ease; }
        @keyframes mStatsIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .media-stat { text-align: center; }
        .media-stat-val { font-size: 1.6rem; font-weight: 800; color: var(--accent); line-height: 1; }
        .media-stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.4); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }

        @media (max-width: 768px) {
          .media-hero { padding: 16px 0; }
          .media-hero-title { font-size: 1.8rem; }
          .media-hero-subtitle { font-size: 0.85rem; }
          .media-hero-badge { font-size: 11px; padding: 5px 12px; }
          .media-filter-pill { padding: 8px 14px; font-size: 12px; }
          .media-hero-stats { gap: 20px; margin-top: 20px; }
          .media-stat-val { font-size: 1.3rem; }
          .media-stat-label { font-size: 0.68rem; }
        }
      ` }} />

      <section className="media-hero">
        <div className="container media-hero-inner">
          <div style={{ textAlign: 'center', paddingTop: '50px', paddingBottom: '30px' }}>
            <div className="media-hero-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              Visual Showcase
            </div>
            <h1 className="media-hero-title">Media <span>Gallery</span></h1>
            <p className="media-hero-subtitle">Explore our projects, installations, and events — see our premium LED lighting solutions in action across India.</p>

            <div className="media-hero-filters">
              {['all', 'image', 'video'].map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={'media-filter-pill' + (type === t ? ' active' : '')}
                >
                  {t === 'all' ? 'All' : t}
                </button>
              ))}
            </div>

            <div className="media-hero-stats">
              <div className="media-stat">
                <div className="media-stat-val">{items.length}+</div>
                <div className="media-stat-label">Media Items</div>
              </div>
              <div className="media-stat">
                <div className="media-stat-val">{items.filter(m => m.type === 'image').length}+</div>
                <div className="media-stat-label">Photos</div>
              </div>
              <div className="media-stat">
                <div className="media-stat-val">{items.filter(m => m.type === 'video').length}+</div>
                <div className="media-stat-label">Videos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section>
        <div className="container">
          {err && (
            <div className="card" style={{ borderColor: '#5b1a1a' }}>
              <div style={{ color: '#ffb3b3' }}>{err}</div>
            </div>
          )}

          {loading ? (
            <div style={{ padding: '2rem 0', color: '#999', textAlign: 'center' }}>Loading...</div>
          ) : (
            <div className="gallery-grid">
              {filtered.map((m) => {
                const preview =
                  m.type === 'image'
                    ? m.file_url
                    : m.thumbnail || placeholderSvgDataUrl({ width: 640, height: 360, text: 'Video' });
                return m.isGallery ? (
                  <div key={m.id} className="gallery-item" style={{ textDecoration: 'none' }}>
                    <img src={preview} alt={m.title} loading="lazy" onError={() => markBroken(m.id)} />
                  </div>
                ) : (
                  <Link key={m.id} to={`/media/${m.id}`} className="gallery-item" style={{ textDecoration: 'none' }}>
                    <img src={preview} alt={m.title} loading="lazy" onError={() => markBroken(m.id)} />
                  </Link>
                );
              })}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <p style={{ color: '#999', fontSize: '1.1rem' }}>
                No media found.
              </p>
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Media;
